import { formatDate } from './formatDate';
import { useQualityTranslation } from './hooks/useQualityTranslation';

function format_pie_dens_data(dens, theme, t) {
  const colors = {
    A: theme.palette.dashboard.acrA,
    B: theme.palette.dashboard.acrB,
    C: theme.palette.dashboard.acrC,
    D: theme.palette.dashboard.acrD,
    not_eval: theme.palette.dashboard.none,
  };

  const sum = Object.values(dens).reduce((total, value) => total + value, 0);
  const data = Object.keys(dens).map((acr) => {
    let category;
    if (acr === 'not_eval') category = t(`Dashboard.not_eval`);
    else category = acr;
    return {
      category: category,
      value: sum ? (dens[acr] / sum).toFixed(3) * 100 : 0,
      color: colors[acr],
      projections: dens[acr],
      categoryFilter: [acr?.toUpperCase()],
    };
  });
  return data;
}

function capitalizedQuality(string) {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
}

function format_pie_risk_data(risk_stats, theme, t) {
  const n_classes = Object.keys(risk_stats).length;
  if (n_classes === 0) return [];

  const colors = {
    low: theme.palette.dashboard.good,
    moderate: theme.palette.dashboard.moderate,
    high: theme.palette.dashboard.insufficient,
    null: theme.palette.dashboard.none,
  };

  const sum = Object.values(risk_stats).reduce((total, elem) => total + elem.count, 0);
  const data = Object.values(risk_stats).map((elem) => {
    const translatedRiskLevel = elem.risk_level
      ? t(`ResultView.${elem.risk_level}`)
      : t('Dashboard.not_evaluated');

    const shortRiskLevel = `${translatedRiskLevel.charAt(0)}`;
    return {
      category: translatedRiskLevel,
      value: sum ? (elem.count / sum).toFixed(3) * 100 : 0,
      color: colors[elem.risk_level],
      category_short: shortRiskLevel,
      projections: elem.count,
      categoryFilter: [elem.risk_level],
    };
  });

  return data;
}

function format_quality_pie_data(qual_stats, theme, t) {
  const n_classes = Object.keys(qual_stats).length;
  if (n_classes === 0) return [];

  const colors = {
    perfect: theme.palette.dashboard.perfect,
    good: theme.palette.dashboard.good,
    moderate: theme.palette.dashboard.moderate,
    inadequate: theme.palette.dashboard.inadequate,
    adequate: theme.palette.dashboard.adequate,
    null: theme.palette.dashboard.none,
    limited: theme.palette.dashboard.moderate,
    insufficient: theme.palette.dashboard.inadequate,
    correct: theme.palette.dashboard.perfect,
    level_1: theme.palette.projections.adequate,
    level_2: theme.palette.projections.moderate,
    level_3: theme.palette.projections.inadequate,
  };

  const sum = Object.values(qual_stats).reduce((total, value) => total + value.count, 0);

  const data = Object.values(qual_stats).map(({ quality_class, count }, i) => {
    const translatedQuality = quality_class
      ? useQualityTranslation(quality_class, t)
      : t('Dashboard.not_evaluated');

    const category = capitalizedQuality(translatedQuality);

    const category_short =
      category !== t('Dashboard.not_eval')
        ? /\d$/.test(category)
          ? `${category.charAt(0)}_${category.split(' ').pop()}`
          : category.charAt(0)
        : category;

    return {
      category,
      category_short,
      projections: count,
      value: sum ? (count / sum).toFixed(3) * 100 : 0,
      color: colors[qual_stats[i]?.quality_class?.toLowerCase()] || theme.palette.dashboard.none,
      categoryFilter: [quality_class?.toLowerCase()],
    };
  });

  return data;
}

function mergeProjectionData(data) {
  const mergedData = {};

  //  CC projections
  if (data?.rcc || data?.lcc) {
    mergedData.cc = {};

    // Get  unique features c
    const ccFeatures = new Set([...Object.keys(data?.rcc || {}), ...Object.keys(data?.lcc || {})]);

    ccFeatures.forEach((feature) => {
      const rccValues = data?.rcc?.[feature]?.values || {};
      const lccValues = data?.lcc?.[feature]?.values || {};

      // Merge values
      const mergedValues = {};
      const allKeys = new Set([...Object.keys(rccValues), ...Object.keys(lccValues)]);

      allKeys.forEach((key) => {
        mergedValues[key] = (rccValues[key] || 0) + (lccValues[key] || 0);
      });

      mergedData.cc[feature] = {
        values: mergedValues,
        filterKey: data?.rcc?.[feature]?.filterKey || data?.lcc?.[feature]?.filterKey,
      };
    });
  }

  //  MLO proj
  if (data?.rmlo || data?.lmlo) {
    mergedData.mlo = {};

    // Get unique feat
    const mloFeatures = new Set([...Object.keys(data?.rmlo || {}), ...Object.keys(data?.lmlo || {})]);

    mloFeatures.forEach((feature) => {
      const rmloValues = data?.rmlo?.[feature]?.values || {};
      const lmloValues = data?.lmlo?.[feature]?.values || {};

      // Merge
      const mergedValues = {};
      const allKeys = new Set([...Object.keys(rmloValues), ...Object.keys(lmloValues)]);

      allKeys.forEach((key) => {
        mergedValues[key] = (rmloValues[key] || 0) + (lmloValues[key] || 0);
      });

      mergedData.mlo[feature] = {
        values: mergedValues,
        filterKey: data?.rmlo?.[feature]?.filterKey || data?.lmlo?.[feature]?.filterKey,
      };
    });
  }

  return mergedData;
}

function format_quality_bar_data(data, t) {
  const formatedData = {};

  // Get merged projection data
  const mergedData = mergeProjectionData(data);

  const featureLabelMapping = {
    NippleCentered: t('Dashboard.NippleCentered'),
    PectoralisCC: t('Dashboard.PectoralisCC'),
    Infram: t('Dashboard.Infram'),
    PectAngle: t('Dashboard.PectAngle'),
    PectLevel: t('Dashboard.PectLevel'),
    PectShape: t('Dashboard.PectShape'),
    skinFolds: t('Dashboard.skinFolds'),
    ParenchymaCuts: t('Dashboard.ParenchymaCuts'),
    ParenchymaCutsMedial: t('Dashboard.ParenchymaCutsMedial'),
    ParenchymaCutsLateral: t('Dashboard.ParenchymaCutsLateral'),
    PectoralisCC: t('Dashboard.pectoralis'),
    Nipple: t('Dashboard.nipple'),
    PNL: t('Dashboard.pnl'),
    elevation: t('ResultView.bucky_height'),
    symmetry: t('ResultView.symmetry'),
    noise: t('ResultView.noise'),
    contrast: t('ResultView.contrast'),
    exposureClass: t('ResultView.exposure'),
    image_labeling: t('ResultView.image_labeling'),
    compressionClass: t('ResultView.compression'),
    VolumeProjectionDiff: t('ResultView.volume_projection_diff'),
    VolumeLastExamDiff: t('ResultView.volume_last_exam_diff'),
    blur: t('ResultView.blur'),
    artifacts: t('ResultView.artifacts'),
    ArtifactsEval: t('ResultView.artifacts'),
  };

  const featureOrderByView = {
    cc: [
      'Nipple',
      'NippleCentered',
      'ParenchymaCutsMedial',
      'ParenchymaCutsLateral',
      'PectoralisCC',
      'PNL',
      'skinFolds',
      'symmetry',
      'compressionClass',
      'blur',
      // 'noise',
      // 'contrast',
      // 'exposureClass',
      // 'image_labeling',
      // 'VolumeProjectionDiff',
      // 'VolumeLastExamDiff',
    ],
    mlo: [
      'Nipple',
      'ParenchymaCuts',
      'Infram',
      'PectAngle',
      'PectLevel',
      'PectShape',
      'skinFolds',
      'symmetry',
      'elevation',
      'compressionClass',
      'blur',
      // 'noise',
      // 'contrast',
      // 'exposureClass',
      // 'image_labeling',
      // 'VolumeProjectionDiff',
      // 'VolumeLastExamDiff',
    ],
  };

  function processDataset(dataset) {
    for (let viewPosition in dataset) {
      if (!formatedData[viewPosition]) formatedData[viewPosition] = [];

      const availableFeatures = Object.keys(dataset[viewPosition]);

      // Determine sorted features based on view type
      let sortedFeatures;
      if (['rcc', 'lcc', 'cc'].includes(viewPosition)) {
        sortedFeatures = featureOrderByView.cc.filter((f) => availableFeatures.includes(f));
      } else if (['rmlo', 'lmlo', 'mlo'].includes(viewPosition)) {
        sortedFeatures = featureOrderByView.mlo.filter((f) => availableFeatures.includes(f));
      } else {
        sortedFeatures = availableFeatures;
      }

      // Build item objects with percentage calculation
      for (let featureName of sortedFeatures) {
        const feature = dataset[viewPosition][featureName];
        if (!feature) continue; // skip if not present

        const item = {
          category: featureLabelMapping[featureName] || featureName,
          filterKey: feature.filterKey,
          ...feature.values,
        };

        const sum = Object.values(feature.values).reduce((acc, cur) => acc + cur, 0);

        for (let k in feature.values) {
          item[k + 'Percent'] = sum > 0 ? ((item[k] / sum) * 100).toFixed(0) : 0;
        }

        formatedData[viewPosition].push(item);
      }
    }
  }

  // Process both original and merged data
  processDataset(data);
  processDataset(mergedData);

  return formatedData;
}

function format_bar_exams_data(exam_stats, t) {
  const { screening, tomo, postop } = exam_stats;
  if (!screening || !tomo || !postop) return [];

  const denoms = [
    screening.screening + screening.diagnostic,
    tomo.tomo + tomo.mammo,
    postop.postop + postop.normal,
  ];

  return [
    {
      dataKey: 'screening',
      category: 'PURP.',
      label1: t('Dashboard.screening'),
      label2: t('Dashboard.diagnostic'),
      value1: ((screening.screening / denoms[0]) * 100).toFixed(2),
      dataItem1: 'yes',
      value2: ((screening.diagnostic / denoms[0]) * 100).toFixed(2),
      dataItem2: 'no',
    },
    {
      dataKey: 'modality',
      category: 'MOD.',
      label1: t('Dashboard.tomo'),
      label2: t('Dashboard.mammo'),
      value1: ((tomo.tomo / denoms[1]) * 100).toFixed(2),
      dataItem1: 'tomo',
      value2: ((tomo.mammo / denoms[1]) * 100).toFixed(2),
      dataItem2: 'mammo',
    },
    {
      dataKey: 'postop',
      category: 'OP.',
      label1: t('Dashboard.postop'),
      label2: t('Dashboard.not_operated'),
      value1: ((postop.postop / denoms[2]) * 100).toFixed(2),
      dataItem1: 'yes',
      value2: ((postop.normal / denoms[2]) * 100).toFixed(2),
      dataItem2: 'no',
    },
  ];
}

function format_lesion_data(data, theme, t) {
  try {
    let { lesionKnown, birads2, birads3, birads4, birads5, total, vessel, not_eval } = data;

    lesionKnown = lesionKnown || 0;
    birads2 = birads2 || 0;
    birads3 = birads3 || 0;
    birads4 = birads4 || 0;
    birads5 = birads5 || 0;
    vessel = vessel || 0;
    total = total || 0;
    not_eval = not_eval || 0;

    return [
      {
        category: t('projections.birads1'),
        category_short: t('projections.birads1'),
        projections: total - birads2 - birads3 - birads4 - birads5 - vessel - not_eval - lesionKnown,
        value: (
          (100 * (total - birads2 - birads3 - birads4 - birads5 - vessel - not_eval - lesionKnown)) /
          total
        ).toFixed(1),
        color: theme.palette.dashboard.perfect,
        categoryFilter: ['birads1'],
      },
      {
        category: t('projections.lesionKnown'),
        category_short: 'Known',
        projections: lesionKnown,
        value: ((100 * lesionKnown) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.lesionKnown,
        categoryFilter: ['birads0'],
      },
      {
        category: 'BI-RADS 2',
        category_short: 'BI-RADS 2',
        projections: birads2,
        value: ((100 * birads2) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.birads2,
        categoryFilter: ['birads2'],
      },
      {
        category: 'BI-RADS 3',
        category_short: 'BI-RADS 3',
        projections: birads3,
        value: ((100 * birads3) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.birads3,
        categoryFilter: ['birads3'],
      },
      {
        category: 'BI-RADS 4',
        category_short: 'BI-RADS 4',
        projections: birads4,
        value: ((100 * birads4) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.birads4,
        categoryFilter: ['birads4'],
      },
      {
        category: 'BI-RADS 5',
        category_short: 'BI-RADS 5',
        projections: birads5,
        value: ((100 * birads5) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.birads5,
        categoryFilter: ['birads5'],
      },
      {
        category: t('projections.vessel'),
        category_short: 'Vessel',
        projections: vessel,
        value: ((100 * vessel) / total).toFixed(1),
        color: theme.palette.diagnosticsClasses.vessel,
        categoryFilter: ['birads0'],
      },
      {
        category: t('Dashboard.not_eval'),
        category_short: t('Dashboard.not_eval'),
        projections: not_eval,
        value: ((100 * not_eval) / total).toFixed(1),
        color: theme.palette.dashboard.none,
        categoryFilter: ['not_eval'],
      },
    ];
  } catch {
    return [];
  }
}

function format_histogram_dose(doses, theme) {
  if (doses?.length === 0) return [];

  const data = doses.map((bar) => {
    let color;
    if (bar?.category < 1.8) {
      color = theme.palette.dashboard.perfect;
    } else if (bar?.category < 2.5) {
      color = theme.palette.dashboard.moderate;
    } else {
      color = theme.palette.dashboard.inadequate;
    }

    return {
      category: bar?.category,
      value: bar?.value ? bar.value : 0.5,
      color,
    };
  });
  return data;
}

function format_histogram_quality(points, theme) {
  if (points?.length === 0) {
    return [];
  }

  return points?.map((bar) => {
    const { category, value = 0.5 } = bar;

    let color;
    if (category >= 90) {
      color = theme.palette.dashboard.perfect;
    } else if (category >= 80) {
      color = theme.palette.dashboard.good;
    } else if (category >= 70) {
      color = theme.palette.dashboard.moderate;
    } else {
      color = theme.palette.dashboard.inadequate;
    }

    return {
      category,
      value,
      color,
    };
  });
}

function addColorData(data, theme) {
  if (data['Revised'] === 0 && data['Approved'] === 0 && data['Remaining'] === 0) {
    return [
      {
        category: 'none',
        value: -1,
        color: 'rgba(255, 255, 255, 0.2)',
      },
    ];
  }
  return Object.keys(data).map((k) => {
    if (k === 'Revised')
      return {
        category: k,
        value: data[k],
        color: theme.palette.primary.accent,
      };
    if (k === 'Approved')
      return {
        category: k,
        value: data[k],
        color: theme.palette.secondary.main,
      };
    if (k === 'Remaining')
      return {
        category: k,
        value: data[k],
        color: theme.palette.primary.dark,
      };
    else
      return {
        category: '',
        value: 0,
        color: 'gray',
      };
  });
}

function format_line_qual_data(data) {
  const n = data.length;
  const data_qual = new Array(n);

  for (let i = 0; i < n; i++) {
    data_qual[i] = {
      date: formatDate(data[i].period),
      uid: data[i].study_id,
      rcc: data[i].score_rcc,
      lcc: data[i].score_lcc,
      rmlo: data[i].score_rmlo,
      lmlo: data[i].score_lmlo,
      name: data[i].name,
    };
  }

  return data_qual;
}

function format_line_comp_data(data) {
  const n = data.length;
  const data_comp = new Array(n);

  for (let i = 0; i < n; i++) {
    data_comp[i] = {
      date: formatDate(data[i].period),
      rcc: data[i].rcc || null,
      lcc: data[i].lcc || null,
      rmlo: data[i].rmlo || null,
      lmlo: data[i].lmlo || null,
      name: data[i].name,
      uid: data[i].exam_id,
    };
  }

  return data_comp;
}

export {
  format_pie_dens_data,
  format_quality_pie_data,
  format_line_comp_data,
  format_quality_bar_data,
  format_bar_exams_data,
  format_histogram_dose,
  format_histogram_quality,
  format_lesion_data,
  addColorData,
  format_line_qual_data,
  mergeProjectionData,
  format_pie_risk_data,
};
